import { type FC, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styled, { createGlobalStyle } from 'styled-components';

import { LD, useLDFlag } from '@dutchie-pos/utils';

import { NotificationsContainerID } from 'src/app/components/notifications';

const getHeight = (fullHeight: boolean, open?: boolean, height?: number): string => {
  if (!open) {
    return '0';
  }

  if (height) {
    return `${String(height)}px`;
  }

  if (fullHeight) {
    return '100%';
  }
  return 'auto';
};

type BottomDrawerProps = {
  children: ReactNode;
  fullHeight?: boolean;
  height?: number;
  marginX?: number;
  marginY?: number;
  onClose: () => void;
  open?: boolean;
  portalId?: string;
  viewportFixed?: boolean;
};

export const BottomDrawer: FC<BottomDrawerProps> = ({
  children,
  fullHeight = false,
  height,
  marginX = 20,
  marginY = 20,
  onClose,
  open,
  portalId,
  viewportFixed = false,
}) => {
  const isShowDriversLicenseEnabled = useLDFlag(LD.MOBILE_CHECKOUT_SHOW_DRIVERS_LICENSE, false);
  const isIdentificationPhotoPreservationV2Enabled = useLDFlag(LD.IDENTIFICATION_PHOTO_PRESERVATION_V2, false);
  const useViewportFixed = viewportFixed && isIdentificationPhotoPreservationV2Enabled;
  const isOverlayFixed = useViewportFixed || isShowDriversLicenseEnabled;

  const component = (
    <>
      {open && <AdjustedNotificationContainer />}
      <OverlayDiv data-testid='bottom-drawer-overlay' open={open} $isOverlayFixed={isOverlayFixed} onClick={onClose} />
      <DrawerDiv
        data-testid='bottom-drawer-panel'
        height={getHeight(fullHeight, open, height)}
        $isViewportFixed={useViewportFixed}
        marginx={marginX}
        marginy={marginY}
        open={open}
      >
        {children}
      </DrawerDiv>
    </>
  );

  const portalEl = document.getElementById(portalId || '');
  if (portalEl) {
    return createPortal(component, portalEl);
  }

  return component;
};

const AdjustedNotificationContainer = createGlobalStyle`
  #${String(NotificationsContainerID)} {
    top: 115px;
  }
`;

const OverlayDiv = styled.div<{ open?: boolean; $isOverlayFixed: boolean }>`
  height: ${({ open }) => (open ? '80%' : '0')};
  position: ${({ $isOverlayFixed }) => ($isOverlayFixed ? 'fixed' : 'absolute')};
  width: 100%;
  z-index: var(--z-index-bottom-drawer);
  pointer-events: auto;
  ${({ $isOverlayFixed }) => $isOverlayFixed && 'top: 0; left: 0;'}
`;

const DrawerDiv = styled.div<{
  height: string;
  $isViewportFixed: boolean;
  marginx: number;
  marginy: number;
  open?: boolean;
}>`
  box-shadow: 0 0 18px rgba(33, 33, 33, 0.2);
  background-color: var(--color-grayscale-white);
  bottom: 0;
  height: ${({ height }) => height};
  max-height: calc(100% - ${({ marginy: marginY }) => marginY}px);
  overflow: auto;
  ${({ $isViewportFixed }) => $isViewportFixed && 'overscroll-behavior: contain;'}
  position: ${({ $isViewportFixed }) => ($isViewportFixed ? 'fixed' : 'absolute')};
  transition: 0.35s ease-in-out;
  width: calc(100% - ${({ marginx: marginX }) => marginX * 2}px);
  left: ${({ marginx: marginX }) => marginX}px;
  z-index: calc(var(--z-index-bottom-drawer) + 1);
  pointer-events: auto;
  border-radius: 20px 20px 0 0;
`;
